import { LOCALES, type CustomTheme, type Locale, type Theme } from '@moussenaires/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { authApi } from '../api/auth.js';
import { ApiClientError } from '../api/client.js';
import { useAuth } from '../context/AuthContext.js';
import { useTheme } from '../context/ThemeContext.js';
import { persistLocale } from '../i18n/index.js';
import { Modal } from './Modal.js';
import { ThemePicker } from './ThemePicker.js';
import './SettingsModal.css';

export function SettingsModal({ onClose }: { onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const theme = useTheme();
  const queryClient = useQueryClient();

  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [avatarUrl, setAvatarUrl] = useState(user?.avatarUrl ?? '');
  const [customTheme, setCustomTheme] = useState<CustomTheme>(user?.themeCustom ?? {});
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState<{
    kind: 'ok' | 'error';
    text: string;
  } | null>(null);

  const profileMutation = useMutation({
    mutationFn: authApi.updateProfile,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me'] }),
  });

  const passwordMutation = useMutation({
    mutationFn: authApi.changePassword,
    onSuccess: () => {
      setPasswordMessage({ kind: 'ok', text: t('settings.saved') });
      setCurrentPassword('');
      setNewPassword('');
    },
    onError: (error: unknown) => {
      const message =
        error instanceof ApiClientError ? t(`errors.${error.code}`) : t('common.somethingWrong');
      setPasswordMessage({ kind: 'error', text: message });
    },
  });

  function saveProfile() {
    profileMutation.mutate({
      displayName: displayName.trim() || undefined,
      avatarUrl: avatarUrl.trim() ? avatarUrl.trim() : '',
    });
  }

  function changeLocale(locale: Locale) {
    void i18n.changeLanguage(locale);
    persistLocale(locale);
    profileMutation.mutate({ locale });
  }

  function changeTheme(nextTheme: Theme) {
    theme.setTheme(nextTheme, nextTheme === 'custom' ? customTheme : undefined);
    profileMutation.mutate({
      theme: nextTheme,
      themeCustom: nextTheme === 'custom' ? customTheme : undefined,
    });
  }

  function updateCustomColor(key: keyof CustomTheme, value: string) {
    const next = { ...customTheme, [key]: value };
    setCustomTheme(next);
    if (theme.theme === 'custom') {
      theme.setTheme('custom', next);
      profileMutation.mutate({ themeCustom: next });
    }
  }

  return (
    <Modal title={t('nav.settings')} onClose={onClose} width="lg">
      <section className="settings-section">
        <h3>{t('settings.profile')}</h3>
        <div className="field">
          <label htmlFor="settings-display-name">{t('settings.displayName')}</label>
          <input
            id="settings-display-name"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            onBlur={saveProfile}
            maxLength={48}
          />
        </div>
        <div className="field">
          <label htmlFor="settings-avatar-url">{t('settings.avatarUrl')}</label>
          <input
            id="settings-avatar-url"
            value={avatarUrl}
            onChange={(event) => setAvatarUrl(event.target.value)}
            onBlur={saveProfile}
            placeholder="https://…"
          />
        </div>
      </section>

      <section className="settings-section">
        <h3>{t('settings.language')}</h3>
        <div className="settings-choice-row">
          {LOCALES.map((locale) => (
            <button
              key={locale}
              type="button"
              className={`btn ${i18n.language === locale ? 'btn-primary' : ''}`}
              onClick={() => changeLocale(locale)}
            >
              {locale === 'fr' ? 'Français' : 'English'}
            </button>
          ))}
        </div>
      </section>

      <section className="settings-section">
        <h3>{t('settings.theme')}</h3>
        <ThemePicker
          theme={theme.theme}
          customTheme={customTheme}
          onSelectTheme={changeTheme}
          onCustomColorChange={updateCustomColor}
        />
      </section>

      <section className="settings-section">
        <h3>{t('settings.password')}</h3>
        <div className="field">
          <label htmlFor="settings-current-password">{t('settings.currentPassword')}</label>
          <input
            id="settings-current-password"
            type="password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="settings-new-password">{t('settings.newPassword')}</label>
          <input
            id="settings-new-password"
            type="password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
          />
        </div>
        {passwordMessage && (
          <p className={passwordMessage.kind === 'error' ? 'field-error' : 'settings-success'}>
            {passwordMessage.text}
          </p>
        )}
        <button
          type="button"
          className="btn btn-primary"
          disabled={!currentPassword || !newPassword || passwordMutation.isPending}
          onClick={() => passwordMutation.mutate({ currentPassword, newPassword })}
        >
          {t('settings.changePassword')}
        </button>
      </section>
    </Modal>
  );
}
